import {
  Box,
  Stack,
  lighten,
  Divider,
  Collapse,
  useTheme,
  Typography,
  ButtonBase,
  type Theme,
  type SxProps,
} from "@mui/material";
import { LuSend } from "react-icons/lu";
import { FaRegClock } from "react-icons/fa6";
import { useTranslation } from "react-i18next";

import { hexToRgba } from "@/global/theme/utils.theme";
import { createState } from "@/global/store/zustand_store";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";

import {
  DeliveryMethodDatePicker,
  useDeliveryMethodDatePicker,
} from "./DeliveryMethodDatePicker";

export const useDeliveryMethodSelectorState = createState<{
  selectedDeliveryMethod: "sendNow" | "sendLater";
}>({
  selectedDeliveryMethod: "sendNow",
});

export function DeliveryMethodSelector() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { set: setDeliveryDate } = useDeliveryMethodDatePicker();
  const { selectedDeliveryMethod, set } = useDeliveryMethodSelectorState();

  function handleClick(method: "sendNow" | "sendLater") {
    if (method === "sendNow") {
      setTimeout(() => {
        setDeliveryDate({ deliveryDate: null, isError: false });
      }, 500);
    }
    set({ selectedDeliveryMethod: method });
  }

  return (
    <Box
      border={`1px solid ${theme.palette.divider}`}
      borderRadius={0.9}
      mb={3}
    >
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        px={2}
        py={2.5}
      >
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"flex-start"}
          spacing={1}
        >
          <LuSend fontSize={"1.6rem"} />
          <Typography
            variant="subtitle2"
            fontWeight={"700"}
            lineHeight={1.3}
            color="text.primary"
          >
            {t(
              "giftCard.checkout.deliveryMethodSelector.deliveryOptionsTitle"
            ) || "Delivery Options"}
          </Typography>
        </Stack>
      </Stack>
      <Divider />
      <Stack p={3} spacing={2}>
        <DeliveryOption
          icon={<LuSend />}
          title={
            t("giftCard.checkout.deliveryMethodSelector.sendNowTitle") ||
            "Send Now"
          }
          description={
            t("giftCard.checkout.deliveryMethodSelector.sendNowDescription") ||
            "Email is sent immediately"
          }
          isSelected={selectedDeliveryMethod === "sendNow"}
          onClick={() => handleClick("sendNow")}
        />

        <DeliveryOption
          icon={<FaRegClock />}
          title={
            t("giftCard.checkout.deliveryMethodSelector.sendLaterTitle") ||
            "Send Later"
          }
          description={
            t(
              "giftCard.checkout.deliveryMethodSelector.sendLaterDescription"
            ) || "Schedule for a specific date"
          }
          isSelected={selectedDeliveryMethod === "sendLater"}
          onClick={() => handleClick("sendLater")}
        />

        <Collapse
          in={selectedDeliveryMethod === "sendLater"}
          mountOnEnter
          unmountOnExit
        >
          <DeliveryMethodDatePicker />
        </Collapse>
      </Stack>
    </Box>
  );
}

type SXMap<T = SxProps<Theme>> = {
  root?: T;
  radio?: T;
  icon?: T;
  title?: T;
  description?: T;
  button?: T;
};

interface DeliveryOptionProps {
  title?: string | React.ReactNode;
  description?: string | React.ReactNode;
  icon?: React.ReactNode;
  isSelected?: boolean;
  isDisabled?: boolean;
  onClick?: () => void;
  slotSx?: SXMap;
}

export function DeliveryOption(props: DeliveryOptionProps) {
  const { title, description, icon, isSelected, isDisabled, onClick, slotSx } =
    props;
  const theme = useTheme();
  const { md } = useCssMediaQuery("up");
  const styles = useStyles({ isSelected, isDisabled, slotSx });

  return (
    <Stack
      direction={"row"}
      alignItems={"center"}
      onClick={onClick}
      p={md ? 2 : 1.5}
      borderRadius={1}
      position={"relative"}
      overflow={"hidden"}
      sx={styles.root}
    >
      <ButtonBase sx={styles.buttonBase} />

      <Stack direction={"row"} alignItems={"center"} spacing={md ? 2 : 1.5}>
        <Box sx={styles.icon}>{icon}</Box>

        <Stack>
          <Typography
            variant="body2"
            fontWeight={600}
            color={theme.palette.grey[900]}
          >
            {title}
          </Typography>

          <Typography variant="body2" color={theme.palette.grey[700]}>
            {description}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
}

function useStyles(props: DeliveryOptionProps): Record<string, SxProps<Theme>> {
  const { isSelected, isDisabled, slotSx } = props;
  const { md } = useCssMediaQuery("up");
  const theme = useTheme();

  return {
    root: {
      cursor: isSelected ? "default" : isDisabled ? "not-allowed" : "pointer",
      backgroundColor: isSelected
        ? hexToRgba(theme.palette.primary.main, 0.05)
        : theme.palette.common.white,
      outline: `1.5px solid ${
        isSelected
          ? lighten(theme.palette.primary.main, 0.2)
          : theme.palette.divider
      }`,
      opacity: isDisabled ? 0.6 : 1,
      WebkitTapHighlightColor: "transparent",
      transition: "all 0.15s ease-in-out",

      "&:hover": {
        outline: `1.5px solid ${
          isSelected
            ? lighten(theme.palette.primary.main, 0.2)
            : lighten(theme.palette.primary.main, 0.1)
        }`,
        "& svg": {
          color: theme.palette.primary.main,
        },
      },

      ...slotSx?.root,
    },
    icon: {
      color: isSelected
        ? theme.palette.primary.main
        : lighten(theme.palette.primary.main, 0.4),
      display: "inline-flex",
      fontSize: md ? "1.6rem" : "1.4rem",
      transform: md ? "none" : "translateY(-50%)",
      transition: "all 0.2s ease-in-out",
      ...slotSx?.icon,
    },
    buttonBase: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: "100%",
      height: "100%",
      opacity: 0.4,
      transition: "all 0.2s ease-in-out",
      ...slotSx?.button,
    },
  };
}
